<template>
    <section class="pago-suplemento">
        <!-- Resumen del suplemento que se está pagando: partido + desglose. -->
        <div v-if="resumen" class="pago-suplemento__resumen">
            <p v-if="resumen.eventName" class="pago-suplemento__partido">
                {{ resumen.eventName }}
            </p>
            <div class="pago-suplemento__linea">
                <span>{{ t('perfil_sga.suplemento_resumen_precio') }}</span>
                <span>{{ formatearPrecio(resumen.price) }}</span>
            </div>
            <div v-if="resumen.priceManagement" class="pago-suplemento__linea">
                <span>{{ t('perfil_sga.suplemento_resumen_gastos') }}</span>
                <span>{{ formatearPrecio(resumen.priceManagement) }}</span>
            </div>
            <div class="pago-suplemento__linea pago-suplemento__linea--total">
                <span>{{ t('perfil_sga.suplemento_resumen_total') }}</span>
                <span>{{ formatearPrecio(resumen.total) }}</span>
            </div>
        </div>

        <!-- Formulario Redsys/CECABANK inyectado por el backend (auto-submit).
             El HTML viene de nuestro propio backend (proxy de confianza, igual
             que en el alta de simpatizante); solo lo localizamos y enviamos. -->
        <div
            v-if="hayFormulario"
            id="formulario-compra-suplemento"
            ref="formularioContainer"
            class="pago-suplemento__formulario"
            v-html="formularioPago"
        ></div>

        <!-- Pago vía Paycomet. El componente navega a /compra-lfp/{session}/{n_pedido}
             al terminar, que reutiliza la pantalla de compra finalizada de SGA
             (el resumen del suplemento se sirve como tipo 'abonos'). -->
        <PagoPaycometComponent
            v-else-if="hayJetId"
            :jet_id="jetId"
            :session_id="sessionId"
            tipo_venta="sga"
            :no-redirigir="false"
        />
    </section>
</template>

<script setup>
    import { ref, computed, onMounted, nextTick } from 'vue';
    import { t } from '@/utils/i18n-helper';
    import PagoPaycometComponent from '@/components/frontend/compra/components/PagoPaycometComponent.vue';

    const props = defineProps({
        jetId: { type: String, default: null },
        formularioPago: { type: String, default: null },
        sessionId: { type: String, default: null },
        /**
         * Desglose del suplemento: { eventName, price, priceManagement, total }.
         * Se pinta encima del formulario de pago para que el usuario sepa qué
         * está pagando.
         */
        resumen: { type: Object, default: null }
    });

    const formularioContainer = ref(null);
    const hayFormulario = computed(() => !!props.formularioPago);
    const hayJetId = computed(() => !!props.jetId);

    const formatearPrecio = precio => {
        if (precio === null || precio === undefined) return '';

        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR'
        }).format(precio);
    };

    // Auto-submit del formulario Redsys/CECABANK inyectado por el backend.
    onMounted(() => {
        if (!hayFormulario.value) return;

        nextTick(() => {
            const form = formularioContainer.value?.querySelector('form');
            if (form) form.submit();
        });
    });
</script>

<style scoped>
/**
 * El desglose se maqueta con CSS propio y no con clases `tw:`: la hoja de
 * Tailwind no llega al bundle de este proyecto, así que esas clases salían
 * sin aplicar y el resumen se veía como texto suelto.
 */
.pago-suplemento {
    margin-bottom: 24px;
}

.pago-suplemento__resumen {
    margin-bottom: 16px;
    padding: 12px;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    background-color: #f8f9fa;
}

.pago-suplemento__partido {
    margin-bottom: 8px;
    font-weight: 600;
    color: #343a40;
}

.pago-suplemento__linea {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    color: #495057;
    margin-top: 4px;
}

.pago-suplemento__linea--total {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #dee2e6;
    font-weight: 700;
    color: #212529;
}

.pago-suplemento__formulario {
    display: none;
}
</style>
